import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useMountEffect } from '@vista-digital/react-tools';
import Glider from 'glider-js';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useOccAccessibilityText } from '../../context/occ/hooks/use-occ-accessibility-text';
import { useOccAssets } from '../../context/occ/hooks/use-occ-assets';
import { deviceHasTouch } from '../../utils/device-has-touch';
import { Icon } from '../icon/icon';
import { getGliderSettings } from './utils/get-glider-settings';
const b = bemClassBuilder('v-carousel');
export const Carousel = observer(props => {
    const gliderRef = useRef();
    const viewport = useRef(null);
    const nextButton = useRef(null);
    const prevButton = useRef(null);
    const dots = useRef(null);
    const { children, config } = props;
    const { settings, touchSettings, showDots, disableScroll, scrollToSlide, scrollToPage, refreshOnUpdate } = config;
    const occAssets = useOccAssets();
    const occAccessibilityText = useOccAccessibilityText();
    const carouselSettings = useMemo(() => getGliderSettings(settings, touchSettings), [settings, touchSettings]);
    const getGliderOptions = useCallback(() => {
        return Object.assign({ arrows: {
                next: nextButton.current,
                prev: prevButton.current
            }, dots: showDots !== false ? dots.current : null, dragVelocity: 1, skipTrack: true }, carouselSettings);
    }, [nextButton, prevButton, showDots, carouselSettings, dots]);
    useMountEffect(() => {
        return () => { var _a; return (_a = gliderRef.current) === null || _a === void 0 ? void 0 : _a.destroy(); };
    });

    const positionGlider = useCallback(() => {
        if (!gliderRef.current)
            return;
        if (scrollToSlide)
            gliderRef.current.scrollItem(scrollToSlide - 1);
        else if (scrollToPage)
            gliderRef.current.scrollItem(scrollToPage - 1, true);
    }, [scrollToSlide, scrollToPage]);

    useEffect(() => {
        if (!viewport.current)
            return;
        requestAnimationFrame(() => {
            if (!viewport.current)
                return;
            gliderRef.current = new Glider(viewport.current, getGliderOptions());
            positionGlider();
        });
    }, [viewport, getGliderOptions, positionGlider, children.length]); // HACK: add children.length to fix changes not triggering a resize


    useEffect(() => {
        var _a, _b;
        if (refreshOnUpdate) {
            (_a = gliderRef.current) === null || _a === void 0 ? void 0 : _a.setOption(getGliderOptions(), true);
            (_b = gliderRef.current) === null || _b === void 0 ? void 0 : _b.refresh(true);
        }
    }, [gliderRef, refreshOnUpdate, getGliderOptions]);
    const hasTouch = deviceHasTouch();
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [_jsxs("div", Object.assign({ className: b.element('wrapper') }, { children: [!hasTouch && !disableScroll && (_jsx("button", Object.assign({ role: "button", className: b.element('button', { prev: true }), ref: prevButton, "aria-label": occAccessibilityText.carouselPreviousButtonAriaLabel }, { children: _jsx(Icon, { name: occAssets.carouselPreviousButtonIconName }) }))), _jsx("div", Object.assign({ className: b.element('viewport', { disableScroll }), ref: viewport }, { children: _jsx("ul", Object.assign({ className: b.element('track') }, { children: children })) })), !hasTouch && !disableScroll && (_jsx("button", Object.assign({ role: "button", className: b.element('button', { next: true }), ref: nextButton, "aria-label": occAccessibilityText.carouselNextButtonAriaLabel }, { children: _jsx(Icon, { name: occAssets.carouselNextButtonIconName }) })))] })), showDots !== false && _jsx("div", { role: "tablist", className: b.element('dots'), ref: dots })] })));
});
Carousel.displayName = 'Carousel';
